import React, { useState, useRef, useEffect, useMemo } from "react";
import i18n from "@/utils/i18n";
import DatePickerIcon from "@/components/icons/datepicker";
import ClockIcon from "@/components/icons/clock";
import { ArrowDown, Clear } from "@/components/icons";
import { DatePickerProps } from "@/types/component";
import Dropdown from "@/components/ui/dropdown";
import "./index.scss";

const weekDays = {
  tr: ["Pt", "Sa", "Ça", "Pe", "Cu", "Ct", "Pz"],
  en: ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"],
};

const months = {
  tr: [
    "Ocak",
    "Şubat",
    "Mart",
    "Nisan",
    "Mayıs",
    "Haziran",
    "Temmuz",
    "Ağustos",
    "Eylül",
    "Ekim",
    "Kasım",
    "Aralık",
  ],
  en: [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ],
};

const DatePicker: React.FC<DatePickerProps> = ({
  label,
  placeholder = i18n.t("datepicker.selectDate"),
  disabled = false,
  error = false,
  errorMessage,
  variant = "standard",
  isRange = false,
  value,
  onChange,
  minDate,
  maxDate,
  format = "dd.MM.yyyy",
  locale = "tr",
  calendarIcon,
  allowClear = false,
  readOnly = false,
  className = "",
  showTime = false,
  ...props
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [focused, setFocused] = useState(false);
  const [hovered, setHovered] = useState(false);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [selectedHour, setSelectedHour] = useState<number>(2);
  const [selectedMinute, setSelectedMinute] = useState<number>(30);
  const [viewMode, setViewMode] = useState<"days" | "months" | "years">("days");

  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Normalize value to always be the correct type
  const normalizedValue = isRange
    ? Array.isArray(value)
      ? value
      : [null, null]
    : Array.isArray(value)
      ? null
      : value;

  // Check if there's any selection
  const hasAnySelection = (): boolean => {
    if (isRange && Array.isArray(normalizedValue)) {
      const [start, end] = normalizedValue;
      return start !== null || end !== null;
    } else {
      return normalizedValue !== null;
    }
  };

  // Format date for display
  const formatDate = (date: Date | null): string => {
    if (!date) return "";

    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();

    if (format === "dd.MM.yyyy") {
      return `${day}.${month}.${year}`;
    }
    return date.toLocaleDateString(locale === "tr" ? "tr-TR" : "en-US");
  };

  const getDisplayValue = (): string => {
    if (isRange && Array.isArray(normalizedValue)) {
      const [start, end] = normalizedValue;
      if (start && end) {
        return `${formatDate(start)} - ${formatDate(end)}`;
      } else if (start) {
        return formatDate(start);
      }
      return "";
    } else if (!isRange && normalizedValue instanceof Date) {
      return formatDate(normalizedValue);
    }
    return "";
  };

  const handleInputClick = () => {
    if (!disabled && !readOnly) {
      const newIsOpen = !isOpen;
      setIsOpen(newIsOpen);
      setFocused(newIsOpen);
      if (newIsOpen) {
        setViewMode("days");
      }
    }
  };

  const handleDateSelect = (selectedDate: Date) => {
    const dateWithTime = new Date(selectedDate);
    if (showTime) {
      dateWithTime.setHours(selectedHour);
      dateWithTime.setMinutes(selectedMinute);
    }

    if (isRange) {
      const currentValue = Array.isArray(normalizedValue)
        ? normalizedValue
        : [null, null];
      const [start, end] = currentValue;

      if (!start || (start && end)) {
        // İlk tarihi seç
        onChange([dateWithTime, null]);
      } else if (start && !end) {
        // İkinci tarihi seç ve sırala
        if (dateWithTime < start) {
          onChange([dateWithTime, start]);
        } else {
          onChange([start, dateWithTime]);
        }
        // Range tamamlandı - showTime yoksa kapat
        if (!showTime) {
          setIsOpen(false);
          setFocused(false);
        }
      }
    } else {
      onChange(dateWithTime);
      // Tek tarih seçimi - showTime yoksa kapat
      if (!showTime) {
        setIsOpen(false);
        setFocused(false);
      }
    }
  };

  const handleClear = (event: React.MouseEvent) => {
    event.stopPropagation();
    if (isRange) {
      onChange([null, null]);
    } else {
      onChange(null);
    }
  };

  // Check if date is in range
  const isDateInRange = (date: Date): boolean => {
    if (!isRange || !Array.isArray(normalizedValue)) return false;

    const [start, end] = normalizedValue;
    if (!start || !end) return false;

    return date >= start && date <= end;
  };

  // Check if date is range end/start
  const isRangeStart = (date: Date): boolean => {
    if (!isRange || !Array.isArray(normalizedValue)) return false;
    const [start] = normalizedValue;
    return start ? date.toDateString() === start.toDateString() : false;
  };

  const isRangeEnd = (date: Date): boolean => {
    if (!isRange || !Array.isArray(normalizedValue)) return false;
    const [, end] = normalizedValue;
    return end ? date.toDateString() === end.toDateString() : false;
  };

  // Check if date is selected
  const isSelected = (date: Date): boolean => {
    if (isRange) {
      return isRangeStart(date) || isRangeEnd(date);
    } else {
      return (
        normalizedValue instanceof Date &&
        date.toDateString() === normalizedValue.toDateString()
      );
    }
  };

  // Generate calendar days - memoized to prevent re-renders
  const calendarDays = useMemo(() => {
    const year = currentMonth.getFullYear();
    const month = currentMonth.getMonth();

    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);

    // Haftanın başlangıç gününü hesapla (TR: Pazartesi, EN: Pazar)
    const dayOfWeek =
      locale === "tr"
        ? (firstDay.getDay() + 6) % 7 // Pazartesi başlangıç
        : firstDay.getDay(); // Pazar başlangıç

    const days = [];

    // Önceki aydan günler ekle (sadece haftanın başını tamamlamak için)
    for (let i = dayOfWeek - 1; i >= 0; i--) {
      const date = new Date(year, month, -i);
      days.push(date);
    }

    // Mevcut ayın tüm günlerini ekle
    for (let day = 1; day <= lastDay.getDate(); day++) {
      days.push(new Date(year, month, day));
    }

    // Sonraki aydan günler ekle (haftayı tamamlamak için)
    const remainingDays = 7 - (days.length % 7);
    if (remainingDays < 7) {
      for (let i = 1; i <= remainingDays; i++) {
        days.push(new Date(year, month + 1, i));
      }
    }

    return days;
  }, [currentMonth, locale]);

  // Check if date is disabled
  const isDateDisabled = (date: Date): boolean => {
    const isOtherMonth = date.getMonth() !== currentMonth.getMonth();
    if (isOtherMonth) return true;

    // Diğer disabled koşulları
    if (minDate && date < minDate) return true;
    if (maxDate && date > maxDate) return true;
    return false;
  };

  // Update hour and minute when value changes
  useEffect(() => {
    if (showTime && normalizedValue) {
      const date =
        isRange && Array.isArray(normalizedValue)
          ? normalizedValue[0]
          : (normalizedValue as Date);

      if (date) {
        setSelectedHour(date.getHours());
        setSelectedMinute(date.getMinutes());
      }
    }
  }, [normalizedValue, showTime, isRange]);

  // Handle click outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
        setFocused(false);
        setViewMode("days");
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen]);

  // Navigation handlers
  const goToPreviousMonth = () => {
    setCurrentMonth(
      new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1),
    );
  };

  const goToNextMonth = () => {
    setCurrentMonth(
      new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1),
    );
  };

  const goToPreviousYear = () => {
    setCurrentMonth(
      new Date(currentMonth.getFullYear() - 1, currentMonth.getMonth()),
    );
  };

  const goToNextYear = () => {
    setCurrentMonth(
      new Date(currentMonth.getFullYear() + 1, currentMonth.getMonth()),
    );
  };

  const goToPreviousDecade = () => {
    setCurrentMonth(
      new Date(currentMonth.getFullYear() - 10, currentMonth.getMonth()),
    );
  };

  const goToNextDecade = () => {
    setCurrentMonth(
      new Date(currentMonth.getFullYear() + 10, currentMonth.getMonth()),
    );
  };

  const handleHeaderClick = () => {
    if (viewMode === "days") {
      setViewMode("months");
    } else if (viewMode === "months") {
      setViewMode("years");
    }
  };

  const handleMonthSelect = (monthIndex: number) => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), monthIndex));
    setViewMode("days");
  };

  const handleYearSelect = (year: number) => {
    setCurrentMonth(new Date(year, currentMonth.getMonth()));
    setViewMode("months");
  };

  // Generate years for year view
  const getYearsRange = (): number[] => {
    const currentYear = currentMonth.getFullYear();
    const startYear = Math.floor(currentYear / 10) * 10;
    const years = [];
    for (let i = startYear; i < startYear + 10; i++) {
      years.push(i);
    }
    return years;
  };

  return (
    <div
      ref={containerRef}
      className={`datepicker-container ${variant} ${focused ? "focused" : ""} ${hovered ? "hovered" : ""} ${error ? "error" : ""} ${disabled ? "disabled" : ""} ${className}`}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      {label && (
        <label
          className={`datepicker-label ${focused ? "focused" : ""} ${error ? "error" : ""} ${disabled ? "disabled" : ""}`}
        >
          {label}
        </label>
      )}

      <div className="datepicker-input-wrapper" onClick={handleInputClick}>
        {/* Sol tarafta icon */}
        <span className="datepicker-start-icon">
          <DatePickerIcon
            width={20}
            height={20}
            color={focused ? "var(--color-primary, #2E3092)" : "#72808F"}
          />
        </span>

        <input
          ref={inputRef}
          className="datepicker-input with-start-icon"
          type="text"
          value={getDisplayValue()}
          placeholder={placeholder}
          readOnly
          disabled={disabled}
          onFocus={() => setFocused(true)}
          {...props}
        />

        {/* Sağ tarafta clear ve arrow */}
        <div className="datepicker-end-icons">
          {allowClear && getDisplayValue() && (
            <span className="datepicker-clear" onClick={handleClear}>
              <Clear width={16} height={16} />
            </span>
          )}
          <span className={`datepicker-arrow-icon ${isOpen ? "open" : ""}`}>
            <ArrowDown
              width={16}
              height={16}
              color={focused ? "var(--color-primary, #2E3092)" : "#72808F"}
            />
          </span>
        </div>
      </div>

      {isOpen && (
        <div className="datepicker-dropdown">
          <div className="datepicker-header">
            <button
              className="datepicker-nav-button"
              onClick={
                viewMode === "days"
                  ? goToPreviousMonth
                  : viewMode === "months"
                    ? goToPreviousYear
                    : goToPreviousDecade
              }
              type="button"
              aria-label={
                viewMode === "days"
                  ? "Previous month"
                  : viewMode === "months"
                    ? "Previous year"
                    : "Previous decade"
              }
            >
              ‹
            </button>

            <span
              className="datepicker-month-year clickable"
              onClick={handleHeaderClick}
            >
              {viewMode === "days" &&
                `${months[locale][currentMonth.getMonth()]} ${currentMonth.getFullYear()}`}
              {viewMode === "months" && currentMonth.getFullYear()}
              {viewMode === "years" &&
                `${getYearsRange()[0]} - ${getYearsRange()[9]}`}
            </span>

            <button
              className="datepicker-nav-button"
              onClick={
                viewMode === "days"
                  ? goToNextMonth
                  : viewMode === "months"
                    ? goToNextYear
                    : goToNextDecade
              }
              type="button"
              aria-label={
                viewMode === "days"
                  ? "Next month"
                  : viewMode === "months"
                    ? "Next year"
                    : "Next decade"
              }
            >
              ›
            </button>
          </div>

          <div className="datepicker-calendar">
            {viewMode === "days" && (
              <>
                <div className="datepicker-weekdays">
                  {weekDays[locale].map((day) => (
                    <div key={day} className="datepicker-weekday">
                      {day}
                    </div>
                  ))}
                </div>

                <div className="datepicker-days">
                  {calendarDays.map((date, index) => {
                    const isCurrentMonth =
                      date.getMonth() === currentMonth.getMonth();
                    const isToday =
                      date.toDateString() === new Date().toDateString();
                    const isDisabled = isDateDisabled(date);
                    const isSelectedDate = isSelected(date);
                    const isInRange = isDateInRange(date);
                    const isRangeStartDate = isRangeStart(date);
                    const isRangeEndDate = isRangeEnd(date);

                    const showToday = isToday && !hasAnySelection();

                    return (
                      <button
                        key={index}
                        className={`datepicker-day ${!isCurrentMonth ? "other-month" : ""} ${showToday ? "today" : ""} ${isSelectedDate ? "selected" : ""} ${isInRange ? "in-range" : ""} ${isRangeStartDate ? "range-start" : ""} ${isRangeEndDate ? "range-end" : ""} ${isDisabled ? "disabled" : ""}`}
                        onClick={() => !isDisabled && handleDateSelect(date)}
                        disabled={isDisabled}
                        type="button"
                      >
                        {date.getDate()}
                      </button>
                    );
                  })}
                </div>
              </>
            )}

            {viewMode === "months" && (
              <div className="datepicker-months">
                {months[locale].map((month, index) => {
                  const isCurrentMonth =
                    index === new Date().getMonth() &&
                    currentMonth.getFullYear() === new Date().getFullYear();
                  const isSelectedMonth = index === currentMonth.getMonth();

                  return (
                    <button
                      key={index}
                      className={`datepicker-month ${isCurrentMonth ? "current" : ""} ${isSelectedMonth ? "selected" : ""}`}
                      onClick={() => handleMonthSelect(index)}
                      type="button"
                    >
                      {month}
                    </button>
                  );
                })}
              </div>
            )}

            {viewMode === "years" && (
              <div className="datepicker-years">
                {getYearsRange().map((year) => {
                  const isCurrentYear = year === new Date().getFullYear();
                  const isSelectedYear = year === currentMonth.getFullYear();

                  return (
                    <button
                      key={year}
                      className={`datepicker-year ${isCurrentYear ? "current" : ""} ${isSelectedYear ? "selected" : ""}`}
                      onClick={() => handleYearSelect(year)}
                      type="button"
                    >
                      {year}
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          {showTime && (
            <div className="datepicker-time-selector">
              <div className="time-dropdown">
                <Dropdown
                  icon={<ClockIcon width={20} height={20} color="#72808F" />}
                  options={Array.from({ length: 24 }, (_, i) => ({
                    value: String(i + 1),
                    label: `${i + 1} ${locale === "tr" ? "Saat" : "Hour"}`,
                  }))}
                  value={String(selectedHour)}
                  selectedLabel={`${selectedHour} ${locale === "tr" ? "Saat" : "Hour"}`}
                  onSelect={(value) => setSelectedHour(Number(value))}
                  placeholder={i18n.t("datepicker.selectHour")}
                />
              </div>

              <div className="time-dropdown">
                <Dropdown
                  icon={<ClockIcon width={20} height={20} color="#72808F" />}
                  options={[
                    {
                      value: "30",
                      label: `30 ${locale === "tr" ? "Dakika" : "Minute"}`,
                    },
                    {
                      value: "60",
                      label: `60 ${locale === "tr" ? "Dakika" : "Minute"}`,
                    },
                  ]}
                  value={String(selectedMinute)}
                  selectedLabel={`${String(selectedMinute).padStart(2, "0")} ${locale === "tr" ? "Dakika" : "Minute"}`}
                  onSelect={(value) => setSelectedMinute(Number(value))}
                  placeholder={i18n.t("datepicker.selectMinute")}
                />
              </div>
            </div>
          )}
        </div>
      )}

      {error && errorMessage && (
        <span className="datepicker-error">{errorMessage}</span>
      )}
    </div>
  );
};

export default DatePicker;
